import React, { useState } from "react";
import { Popover } from "@headlessui/react";
import { usePopper } from "react-popper";
import { Info } from "lucide-react";

/**
 * Props for ExitsBreakdownPopover component
 */
interface ExitsBreakdownPopoverProps {
  /** Number of generic exits */
  exits: number;
  /** Number of waste/loss exits */
  waste: number;
}

/**
 * ExitsBreakdownPopover Component
 * 
 * Displays a popover with breakdown of exits (generic + waste) when hovering
 * over the info icon in the exits column of the inventory summary table.
 * 
 * @param {ExitsBreakdownPopoverProps} props - Component props
 * @returns {JSX.Element} Rendered popover component
 */
export const ExitsBreakdownPopover: React.FC<ExitsBreakdownPopoverProps> = ({
  exits,
  waste,
}) => {
  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "top",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 8],
        },
      },
    ],
  });

  const total = exits + waste;

  return (
    <Popover className="relative inline-flex">
      {({ open }) => (
        <>
          <Popover.Button
            ref={setReferenceElement}
            className="ml-1 inline-flex items-center rounded-full p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none"
          >
            <Info className="h-4 w-4" />
          </Popover.Button>

          <Popover.Panel
            ref={setPopperElement}
            style={styles.popper}
            {...attributes.popper}
            className="z-50 w-48 rounded-lg border border-gray-200 bg-white shadow-lg"
          >
            <div className="p-3">
              <div className="mb-2 text-center text-xs font-semibold text-gray-500">
                Desglose de Salidas
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Salidas genéricas:</span>
                  <span className="font-medium text-gray-900">{exits}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Merma:</span>
                  <span className="font-medium text-red-600">{waste}</span>
                </div>
                <div className="border-t border-gray-200 pt-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-gray-700">Total:</span>
                    <span className="font-bold text-gray-900">{total}</span>
                  </div>
                </div>
              </div>
            </div>
          </Popover.Panel>
        </>
      )}
    </Popover>
  );
};
